// Phase 53 VIS-03/04 — StudioGallery (consolidated single section)
// Phase 68 VIS-10 — lightbox integration : tiles become buttons, click opens
// StudioGalleryLightbox with the matching alt text.
//
// Mosaic gallery of studio photos (Phase 51 vite-imagetools barrel),
// rendered with CSS Grid + `grid-auto-flow: dense` for an asymmetric "mosaïque"
// feel WITHOUT a JS library (no Pinterest masonry, no carousel).
//
// Single-variant component: ALL 7 non-hero photos (`'02','03','06','07','08',
// '09','10'`) render in this gallery. studio01 stays exclusively in the hero
// above the fold to avoid duplicate first-impression. studio10 (douche) is
// pinned last as the closing "amenity" beat — see VARIANT_PHOTOS order.
//
// VIS-06 (equipment showcase) + VIS-07 (amenity callouts) are now absorbed
// into this single gallery instead of dedicated sections — operator chose to
// consolidate after seeing the real photos render together.
//
// VIS-08 motion-safe: gating hover transforms behind `motion-safe:` is part
// of the parent `Home.tsx` sweep. The new tile <button> wrappers carry a
// `motion-safe:hover:scale-[1.02]` micro-interaction (gated, so reduced-motion
// users see static tiles).
//
// Refs: phase 53 (gallery), phase 68 (lightbox), milestone v1.7/v1.8

import { useState } from 'react'
import { studioPictures, type StudioId } from '../../assets/studio'
import { useLanguage } from '../../i18n/LanguageProvider'
import { fr } from '../../i18n/locales/fr'
import StudioGalleryLightbox from './StudioGalleryLightbox'

// Single ordered list. Stable order = stable visual.
// studio01 deliberately excluded (it is the hero above the fold).
// studio10 (douche) deliberately last (closing amenity beat).
export const GALLERY_PHOTOS: ReadonlyArray<StudioId> = [
  '02', '03', '04', '05', '06', '07', '08', '09', '10',
] as const

// Per-photo alt text.
// Rules (VIS-04, WCAG 2.2 AA + Sentry Replay PII guard):
//   - ≤125 chars (enforced by Vitest spec)
//   - descriptive: <matériel> + <cadrage> + <ambiance>
//   - NO "Photo de…" boilerplate
//   - NO proper nouns of people (PII)
//   - Brand names (Rogue, Concept2) are OK — they describe the equipment
//
// Phase 95 Plan 95-07 — les valeurs ont déménagé au catalogue (`studio.alt.*`)
// parce qu'un texte alternatif est du texte destiné à un humain : le laisser en
// français sur une page anglaise est une régression d'accessibilité invisible à
// l'œil ET aux tests de texte visible (T-95-14).
//
// ALT_TEXT reste exporté et reste la FACE FRANÇAISE de ces clés : c'est sur lui
// que porte le contrat VIS-04 (≤125 caractères, pas de « Photo de », pas de nom
// propre de personne). Il RELIT le catalogue, il ne le recopie pas — deux
// copies dériveraient, et c'est exactement la classe de bug que la relecture
// des tests ne rattrape jamais.
export const ALT_TEXT: Record<StudioId, string> = {
  '01': fr['studio.alt.01'],
  '02': fr['studio.alt.02'],
  '03': fr['studio.alt.03'],
  '04': fr['studio.alt.04'],
  '05': fr['studio.alt.05'],
  '06': fr['studio.alt.06'],
  '07': fr['studio.alt.07'],
  '08': fr['studio.alt.08'],
  '09': fr['studio.alt.09'],
  '10': fr['studio.alt.10'],
}

export interface StudioGalleryProps {
  /**
   * Optional override of which photos to render. Defaults to the full gallery.
   */
  photos?: ReadonlyArray<StudioId>
}

export default function StudioGallery({ photos = GALLERY_PHOTOS }: StudioGalleryProps) {
  const { t } = useLanguage()
  // Selected photo id drives the lightbox open state. null = closed.
  // We deliberately store the StudioId (not just isOpen) so the lightbox knows
  // WHICH photo to render — supports any future "next/prev" flow without
  // restructuring the parent.
  const [selected, setSelected] = useState<StudioId | null>(null)

  return (
    <>
      <div
        role="list"
        aria-label={t('studio.gallery.aria')}
        // CSS Grid mosaic:
        //   - 1 col mobile, 3 cols desktop
        //   - `grid-auto-flow: dense` packs varied-size tiles tightly without
        //     a JS layout engine — the "mosaïque" feel comes from natural
        //     aspect-ratio variation across photos, not algorithm.
        //   - Arbitrary-value `lg:[grid-template-columns:…]` works in Tailwind v4
        //     and avoids touching the (CSS-based, no JS config) tokens.
        className="grid gap-4 grid-cols-1 lg:[grid-template-columns:repeat(3,minmax(0,1fr))] lg:[grid-auto-flow:dense]"
      >
        {photos.map((id) => {
          const pic = studioPictures[id]
          // `studio.alt.${id}` est un type littéral de gabarit : les huit clés
          // existant dans le catalogue, TS le vérifie sans cast. Une photo
          // ajoutée sans sa clé casserait la compilation, pas le rendu.
          const alt = t(`studio.alt.${id}`)
          const zoomLabel = t('studio.zoom', { alt })
          return (
            // <picture> stays role="listitem" to preserve the Phase 53 contract
            // (test L4: tiles are role="listitem", count = 7). The new <button>
            // wraps the inner img+sources so the click target is the entire
            // tile and screen readers announce "Agrandir la photo : <alt>".
            <picture
              key={id}
              role="listitem"
              className="relative block rounded-[8px] overflow-hidden border border-border bg-surface"
            >
              <source type="image/avif" srcSet={pic.sources.avif} />
              <source type="image/webp" srcSet={pic.sources.webp} />
              {/* The <img> is the only child a <picture> ultimately needs; the
                  click affordance is implemented by wrapping the picture in a
                  block-sized button inside the listitem semantic. We render
                  the button as a sibling overlay (absolute over the picture)
                  to keep the existing AVIF/WebP test selectors stable.
                  Cleaner alternative considered : wrap <picture> inside
                  <button>. Rejected because the existing test #2 queries
                  `picture source` and asserts AVIF + WebP sources — that still
                  works with both layouts, BUT the implementation reads more
                  naturally with the button as a stretched overlay. */}
              <img
                src={pic.img.src}
                width={pic.img.w}
                height={pic.img.h}
                alt={alt}
                loading="lazy"
                decoding="async"
                className="w-full h-auto object-cover"
              />
              <button
                type="button"
                onClick={() => setSelected(id)}
                aria-label={zoomLabel}
                // Stretches over the entire <picture> tile (which is
                // `position: relative` via the listitem outer styles? actually
                // <picture> by default isn't positioned — wrap is fine since
                // we want the button to BE the click target sized like the
                // tile). The button is rendered as block w-full, sitting
                // BELOW the img in DOM order but visually overlapping via
                // negative margin would be hacky. Cleaner : use absolute
                // positioning with inset-0. For that we need the picture
                // itself to be `relative`. Tailwind: adding `relative` to the
                // existing picture className.
                className="absolute inset-0 w-full h-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-accent focus:ring-inset motion-safe:transition-transform motion-safe:hover:scale-[1.02]"
              >
                <span className="sr-only">{zoomLabel}</span>
              </button>
            </picture>
          )
        })}
      </div>
      <StudioGalleryLightbox
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        photoId={selected}
        alt={selected !== null ? t(`studio.alt.${selected}`) : ''}
      />
    </>
  )
}
